import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLayoutStore } from '../../stores/layoutStore';

export interface PageTitleProps {
  /** Icon element (rendered in the orange badge) */
  icon: React.ReactNode;
  title: string;
  /** Omit or pass empty string to hide; a node allows emphasis or links */
  subtitle?: React.ReactNode;
  /** Optional node after the title (e.g. status badge) */
  titleSuffix?: React.ReactNode;
}

/**
 * Page icon + title + subtitle, portaled into the middle of AppHeader (replacing the breadcrumbs).
 * Renders inline only when the app header isn't mounted (e.g. fullscreen mode).
 * Use directly on pages that don't wrap their content in PageLayout.
 */
export const PageTitle: React.FC<PageTitleProps> = ({ icon, title, subtitle, titleSuffix }) => {
  const pageHeaderSlot = useLayoutStore((s) => s.pageHeaderSlot);
  const registerPageHeader = useLayoutStore((s) => s.registerPageHeader);
  const inHeader = pageHeaderSlot != null;

  useEffect(() => {
    if (!inHeader) return;
    return registerPageHeader();
  }, [inHeader, registerPageHeader]);

  const titleBlock = (
    <header className="mb-0 min-w-0">
      <div className="flex items-center min-w-0" style={{ gap: '0.6rem', marginBottom: 0 }}>
        <div
          className="flex items-center justify-center shrink-0 rounded bg-primary/10 text-primary [&_svg]:h-[0.8rem] [&_svg]:w-[0.8rem]"
          style={{ width: '1rem', height: '1rem' }}
        >
          {icon}
        </div>
        <h1
          className="font-bold text-foreground truncate min-w-0"
          style={{ fontSize: '1rem', lineHeight: '1.5rem' }}
          title={title}
        >
          {title}
        </h1>
        {titleSuffix}
      </div>
      {subtitle ? (
        <p
          className="text-muted-foreground truncate"
          style={{ fontSize: '0.7rem', lineHeight: '1rem' }}
          title={typeof subtitle === 'string' ? subtitle : undefined}
        >
          {subtitle}
        </p>
      ) : null}
    </header>
  );

  return pageHeaderSlot ? createPortal(titleBlock, pageHeaderSlot) : titleBlock;
};

export default PageTitle;
